import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import FactionCardRoster from '../components/faction/FactionCardRoster';
import { mergeFactionWrestlersWithDetails } from '../utils/factionWrestlers';
import { calculateFactionTotalScore } from '../utils/factionScoring';
import '../styles/UserPage.css';

const getTimestampValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

function UserPage() {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [rosterSections, setRosterSections] = useState([]);

  useEffect(() => {
    const loadUserPage = async () => {
      try {
        setLoading(true);
        setError('');

        const userSnap = await getDoc(doc(db, 'users', userId));
        if (!userSnap.exists()) {
          setError('Utente non trovato');
          setLoading(false);
          return;
        }

        const userData = userSnap.data();
        setUserProfile({
          id: userSnap.id,
          username: userData.username || userData.displayName || userData.email || 'Utente',
          email: userData.email || '',
          profileImageUrl: userData.profileImageUrl || null,
        });

        const [factionsSnap, rostersSnap, wrestlersSnap] = await Promise.all([
          getDocs(collection(db, 'factions')),
          getDocs(collection(db, 'rosters')),
          getDocs(collection(db, 'wrestlers')),
        ]);

        const wrestlersMap = {};
        wrestlersSnap.forEach((wDoc) => {
          const wrestler = wDoc.data();
          wrestlersMap[wDoc.id] = {
            ...wrestler,
            id: wDoc.id,
            imageUrl: wrestler.imageUrl || '/assets/default-wrestler.png',
            isInjured: wrestler.isInjured ?? wrestler.is_injured ?? wrestler.injured ?? false,
          };
        });

        const rostersMap = {};
        rostersSnap.forEach((rosterDoc) => {
          rostersMap[rosterDoc.id] = { id: rosterDoc.id, ...rosterDoc.data() };
        });

        const ownedFactions = factionsSnap.docs
          .map((factionDoc) => ({ id: factionDoc.id, ...factionDoc.data() }))
          .filter((faction) => faction.ownerUid === userId);

        const userRosterIds = Array.isArray(userData.rosterIds) ? userData.rosterIds : [];
        const factionRosterIds = ownedFactions.map((faction) => faction.rosterId).filter(Boolean);
        const rosterIds = Array.from(new Set([...userRosterIds, ...factionRosterIds]));

        const sections = rosterIds
          .map((rosterId) => {
            const roster = rostersMap[rosterId];
            if (!roster) return null;

            const faction = ownedFactions.find((item) => item.rosterId === rosterId) || null;
            if (!faction) {
              return {
                roster,
                faction: null,
                members: [],
              };
            }

            const members = mergeFactionWrestlersWithDetails(faction, wrestlersMap);
            const totalScore = calculateFactionTotalScore({ faction, roster, members });

            return {
              roster,
              faction: {
                ...faction,
                totalScore,
                creditsLeft: faction.creditsLeft || 0,
              },
              members,
            };
          })
          .filter(Boolean)
          .sort((a, b) => getTimestampValue(b.roster.createdAt) - getTimestampValue(a.roster.createdAt));

        setRosterSections(sections);
      } catch (loadError) {
        console.error(loadError);
        setError('Errore durante il caricamento della pagina utente');
      } finally {
        setLoading(false);
      }
    };

    loadUserPage();
  }, [userId]);

  const rosterCount = rosterSections.length;
  const factionCount = useMemo(
    () => rosterSections.filter((section) => section.faction).length,
    [rosterSections]
  );

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger text-center mx-auto" style={{ maxWidth: '560px' }}>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
        <div className="text-center mt-3">
          <Link to="/factions" className="btn btn-outline-light">
            <i className="bi bi-arrow-left me-2"></i>
            Torna alle fazioni
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="user-page py-4 py-lg-5">
      <div className="container">
        <div className="user-page__hero mb-4 mb-lg-5">
          <div className="user-page__hero-content">
            <div className="user-page__profile">
              {userProfile?.profileImageUrl ? (
                <img
                  src={userProfile.profileImageUrl}
                  alt={userProfile.username}
                  className="user-page__avatar"
                />
              ) : (
                <div className="user-page__avatar user-page__avatar--placeholder">
                  <i className="bi bi-person-fill"></i>
                </div>
              )}

              <div className="min-w-0">
                <h1 className="user-page__title">{userProfile?.username}</h1>
                {userProfile?.email ? (
                  <div className="user-page__subtitle">{userProfile.email}</div>
                ) : null}
              </div>
            </div>

            <div className="user-page__stats">
              <div className="user-page__stat-card">
                <span className="user-page__stat-label">Roster</span>
                <strong>{rosterCount}</strong>
              </div>
              <div className="user-page__stat-card">
                <span className="user-page__stat-label">Fazioni</span>
                <strong>{factionCount}</strong>
              </div>
            </div>
          </div>
        </div>

        {rosterSections.length === 0 ? (
          <div className="user-page__empty">
            <i className="bi bi-collection fs-1 text-brand-secondary mb-3"></i>
            <h5 className="brand-title mb-2">Nessun roster disponibile</h5>
            <p className="text-muted-brand mb-0">Questo utente non risulta associato a roster o fazioni.</p>
          </div>
        ) : (
          <div className="user-page__list">
            {rosterSections.map(({ roster, faction, members }) => (
              <section key={roster.id} className="user-page__section">
                <div className="user-page__section-head">
                  <div>
                    <button
                      type="button"
                      className="user-page__roster-link"
                      onClick={() => navigate(`/rosters/${roster.id}`)}
                    >
                      {roster.name}
                    </button>
                  </div>
                  <span className={`badge px-3 py-2 rounded-pill ${roster.gameType === 'Road To' ? 'bg-brand-accent text-brand-accent' :
                      roster.gameType === 'Competitivo' ? 'bg-brand-secondary text-brand-primary' :
                        roster.gameType === 'Indipendente' ? 'bg-brand-primary text-brand-primary-ultralight' :
                          'bg-secondary text-white'
                    }`}>
                    {roster.gameType || 'Modalità non disponibile'}
                  </span>
                </div>

                {faction ? (
                  <div className="user-page__faction-card-wrap">
                    <FactionCardRoster
                      faction={faction}
                      members={members}
                      showScore={true}
                    />
                  </div>
                ) : (
                  <div className="user-page__section-empty">
                    <i className="bi bi-people me-2"></i>
                    Nessuna fazione in questo roster
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserPage;
